import { HabitCategory, HabitBadge } from '@/types/habits';
import { useLanguage } from '@/contexts/LanguageContext';

export const BADGE_LEVELS = {
  bronze: { daysRequired: 30, color: 'text-orange-600' },
  silver: { daysRequired: 60, color: 'text-slate-400' },
  gold: { daysRequired: 90, color: 'text-yellow-500' }
} as const;

// Define the order of categories
export const HABIT_CATEGORY_ORDER: HabitCategory[] = [
  'Exercises',
  'Nutrition',
  'Sleep',
  'Stress',
  'Relations',
  'Addictions'
];

// Define category icons
export const CATEGORY_ICONS = {
  Exercises: '🏃‍♂️',
  Nutrition: '🥗',
  Sleep: '😴',
  Stress: '😰',
  Relations: '❤️',
  Addictions: '📱'
} as const;

// Define badge icons
export const BADGE_ICONS = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇'
} as const;

// Create badge configurations for each category and level
export const habitBadges: Record<HabitCategory, { 
  icon: string; 
  badges: Record<keyof typeof BADGE_LEVELS, HabitBadge[]> 
}> = {
  Exercises: {
    icon: CATEGORY_ICONS.Exercises,
    badges: {
      bronze: [{ id: 'exercises-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'exercises-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'exercises-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  },
  Nutrition: {
    icon: CATEGORY_ICONS.Nutrition,
    badges: {
      bronze: [{ id: 'nutrition-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'nutrition-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'nutrition-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  },
  Sleep: {
    icon: CATEGORY_ICONS.Sleep,
    badges: {
      bronze: [{ id: 'sleep-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'sleep-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'sleep-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  },
  Stress: {
    icon: CATEGORY_ICONS.Stress,
    badges: {
      bronze: [{ id: 'stress-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'stress-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'stress-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  },
  Relations: {
    icon: CATEGORY_ICONS.Relations,
    badges: {
      bronze: [{ id: 'relations-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'relations-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'relations-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  },
  Addictions: {
    icon: CATEGORY_ICONS.Addictions,
    badges: {
      bronze: [{ id: 'addictions-bronze', level: 'bronze', name: '', icon: BADGE_ICONS.bronze, daysRequired: BADGE_LEVELS.bronze.daysRequired }],
      silver: [{ id: 'addictions-silver', level: 'silver', name: '', icon: BADGE_ICONS.silver, daysRequired: BADGE_LEVELS.silver.daysRequired }],
      gold: [{ id: 'addictions-gold', level: 'gold', name: '', icon: BADGE_ICONS.gold, daysRequired: BADGE_LEVELS.gold.daysRequired }]
    }
  }
};

export function getBadgeForDays(category: HabitCategory, days: number, t: (key: string) => string): HabitBadge {
  const categoryIcon = CATEGORY_ICONS[category];
  
  if (days >= BADGE_LEVELS.gold.daysRequired) {
    return {
      id: `${category.toLowerCase()}-gold`,
      level: 'gold',
      name: `${categoryIcon} ${t(`habits.badges.${category.toLowerCase()}.master`)}`,
      icon: BADGE_ICONS.gold,
      daysRequired: BADGE_LEVELS.gold.daysRequired
    };
  } else if (days >= BADGE_LEVELS.silver.daysRequired) {
    return {
      id: `${category.toLowerCase()}-silver`,
      level: 'silver',
      name: `${categoryIcon} ${t(`habits.badges.${category.toLowerCase()}.warrior`)}`,
      icon: BADGE_ICONS.silver,
      daysRequired: BADGE_LEVELS.silver.daysRequired
    };
  } else {
    return {
      id: `${category.toLowerCase()}-bronze`,
      level: 'bronze',
      name: `${categoryIcon} ${t(`habits.badges.${category.toLowerCase()}.beginner`)}`,
      icon: BADGE_ICONS.bronze,
      daysRequired: BADGE_LEVELS.bronze.daysRequired
    };
  }
}

export function getNextBadge(category: HabitCategory, currentLevel: string, t: (key: string) => string): HabitBadge | null {
  const categoryIcon = CATEGORY_ICONS[category];
  
  if (currentLevel === 'bronze') {
    return {
      id: `${category.toLowerCase()}-silver`,
      level: 'silver',
      name: `${categoryIcon} ${t(`habits.badges.${category.toLowerCase()}.warrior`)}`,
      icon: BADGE_ICONS.silver,
      daysRequired: BADGE_LEVELS.silver.daysRequired
    };
  } else if (currentLevel === 'silver') {
    return {
      id: `${category.toLowerCase()}-gold`,
      level: 'gold',
      name: `${categoryIcon} ${t(`habits.badges.${category.toLowerCase()}.master`)}`,
      icon: BADGE_ICONS.gold,
      daysRequired: BADGE_LEVELS.gold.daysRequired
    };
  }
  return null;
}